import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth } from '@clerk/clerk-react';

import { addAuthInterceptor } from '../utils/apiService';
import { getUserPreferenceService } from "../utils/userService"
import { getAssistantService } from '../utils/assistantService';
import { getAllTasksService, getTasksByProjectIdsService } from '../utils/taskService';
import { getChatsByProjectAndTaskIdsService } from '../utils/chatService';
import { getAllProjectsService } from '../utils/projectService';
import { getAllTaskCategoriesService } from '../utils/taskCategoryService';
import { updateTask } from '../reducers/taskReducer';
import { useLoading } from '../components/Auth/LoadingProvider';
import { logout } from '../store';

export const useAuthData = () => {
  const { isSignedIn, userId, getToken } = useAuth();
  const dispatch = useDispatch();
  const { setIsDataLoading } = useLoading();
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (isSignedIn && getToken) {
      addAuthInterceptor(getToken);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => {
    const loadUserData = async () => {
      if (isSignedIn && userId && !isInitialLoadComplete) {
        setIsDataLoading(true);
        
        try {
          // Awaited before everything else on purpose: fetchAssistant's dbAddUser
          // is the only writer of new `users` rows, and getUserPreferenceService
          // races it for a brand-new sign-in (fd54b76). Keep the ordering.
          // Its FAILURE, though, must not be fatal -- one dead endpoint used to
          // take the whole app down with it (prg-0qc.2).
          let assistantResponse = null;
          try {
            assistantResponse = await getAssistantService(userId);
          } catch (error) {
            console.warn('Assistant unavailable; continuing without chat:', error?.message || error);
          }

          const responses = await Promise.all([
            getAllProjectsService(getToken, userId),
            getAllTasksService(getToken, userId),
            getAllTaskCategoriesService(),
            getUserPreferenceService(),
          ]);

          const [projects, tasks, projectCategories] = await Promise.all(
            responses.map(r => r.data)
          );

          const assistant = assistantResponse?.data;
          if (assistant?.assistantId) {
            dispatch({ type: 'assistant/createAssistant', payload: {id: assistant.assistantId, name: assistant.assistantName} });
          } else {
            dispatch({ type: 'assistant/setAssistantUnavailable', payload: { reason: assistant?.degradedReason } });
          }
          dispatch({ type: 'projectList/setProjects', payload: projects });
          dispatch({ type: 'taskList/setTasks', payload: tasks });
          dispatch({ type: 'taskCategoryList/setProjectCategories', payload: projectCategories });

          const projectIds = projects.projects.map((p) => p.id);
          const taskIds = tasks.tasks.map((t) => t.id);

          let projectTasks = [];
          if (projectIds.length > 0) {
            projectTasks = await getTasksByProjectIdsService(getToken, projectIds);
            dispatch({ type: 'taskList/addTasks', payload: projectTasks });
          }

          if (taskIds.length > 0 || projectIds.length > 0) {
            const chats = await getChatsByProjectAndTaskIdsService(getToken, { projectIds, taskIds });
            dispatch({ type: 'chats/setChats', payload: chats });
          
            // Merge tasks and projectTasks. Once merged, remove any task actions that were created using yesterday's date.
            const tasksList = tasks?.tasks || [];
            const projectTasksList = projectTasks?.tasks || [];
            const mergedTasks = [...tasksList, ...projectTasksList];

            mergedTasks.forEach(task => {
              if (task.actions) {
                // Find any actions that were created using yesterdays Digest and remove it from the task.
                const currentActions = task.actions.filter(action => {
                  const actionCreatedDate = new Date(action.created_at);
                  const taskUpdatedDate = new Date(task.updated_at);
                  return (actionCreatedDate >= taskUpdatedDate) || task.completed;
                });
                dispatch(updateTask({ ...task, actions: currentActions.length > 0 ? currentActions : undefined }));
              }
            });
          }

          setIsInitialLoadComplete(true);
          setIsDataLoading(false);
        } catch (error) {
          console.error('Error loading user data:', error);
          // Completing the load on failure is what makes the failure visible:
          // App.js gates on `isDataLoading || !isInitialLoadComplete`, so
          // leaving this false renders LoadingSpinner forever with no error
          // and no way out (prg-di7).
          setIsInitialLoadComplete(true);
          setIsDataLoading(false);
        }
      } else if (!isSignedIn) {
        // Clear redux store on logout
        dispatch(logout());
        setIsDataLoading(false);
        setIsInitialLoadComplete(false);
      }
    };

    loadUserData();
  }, [isSignedIn, userId, getToken, dispatch, setIsDataLoading, isInitialLoadComplete]);

  return { isSignedIn, isInitialLoadComplete };
};
